// AppError: única forma de tirar errores tipados desde server actions / API
// routes. El wrapper (`action()`, `apiHandler()`) lo cachea, lo reporta a
// observability y lo mapea a `ActionResult` o `NextResponse` según corresponda.
//
// `code` es el tipo de error desde el punto de vista de UX (qué canal usar:
// field error, alert, toast, boundary). Los detalles del "por qué" van en
// `meta` (no se muestran al usuario, sí se reportan a observability).

export type ErrorCode =
  | "validation" // input no pasa zod → field errors o alert
  | "unauthorized" // no logueado → redirect a /login
  | "forbidden" // logueado pero sin permiso → alert
  | "not_found" // recurso no existe / no visible → not-found state
  | "conflict" // duplicado / colisión → alert + sugerir refrescar
  | "invalid_state" // transición no permitida → alert con contexto
  | "external_service" // OpenAI/Deepgram/etc. caídos → toast + retry
  | "internal"; // bug nuestro → toast genérico + reportId

export interface AppErrorOptions {
  fieldErrors?: Record<string, string>;
  cause?: unknown;
  meta?: Record<string, unknown>;
  details?: Record<string, unknown>;
}

export class AppError extends Error {
  readonly code: ErrorCode;
  readonly fieldErrors?: Record<string, string>;
  readonly meta?: Record<string, unknown>;
  // A diferencia de `meta` (solo observability), `details` SÍ viaja al cliente
  // en el ActionResult: es el payload estructurado para que la UI enriquezca
  // el error (ej: linkear al caso que ya usa un N° duplicado). No poner acá
  // nada que el usuario no pueda ver.
  readonly details?: Record<string, unknown>;

  constructor(code: ErrorCode, message: string, options?: AppErrorOptions) {
    super(message, options?.cause ? { cause: options.cause } : undefined);
    this.name = "AppError";
    this.code = code;
    this.fieldErrors = options?.fieldErrors;
    this.meta = options?.meta;
    this.details = options?.details;
  }
}

export function isAppError(err: unknown): err is AppError {
  return err instanceof AppError;
}

// Códigos "esperables": resultados normales de UX (input inválido, no logueado,
// sin permiso, recurso inexistente/no visible), NO bugs. No se reportan a error
// tracking — sólo ensucian el dashboard con falsos positivos y disparan alertas
// por un permiso denegado. El resto (conflict, invalid_state, external_service,
// internal) sí se reporta: pueden señalar carreras, servicios caídos o bugs.
const NEVER_REPORTED_CODES: ReadonlySet<ErrorCode> = new Set<ErrorCode>([
  "validation",
  "unauthorized",
  "forbidden",
  "not_found",
]);

export function shouldReportToErrorTracking(err: AppError): boolean {
  return !NEVER_REPORTED_CODES.has(err.code);
}

// Next.js usa "errores" internos con un `digest` especial como mecanismo de
// control flow para redirect()/notFound() — no son errores reales. Si un
// wrapper (`apiHandler()`, `action()`) los atrapa y los reporta a observability
// o los mapea a un 500, rompe el redirect y ensucia el error tracking. Por eso
// ambos wrappers deben re-tirarlos antes de cualquier otra cosa.
export function isNextControlFlowError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const digest = (err as { digest?: unknown }).digest;
  if (typeof digest !== "string") return false;
  return digest === "NEXT_NOT_FOUND" || digest.startsWith("NEXT_REDIRECT");
}

// Helpers de uso frecuente — evitan repetir `new AppError("not_found", "...")`
// con mensajes inconsistentes. El `meta.subcode` queda como string libre para
// observability (tag fino en PostHog).

export function notFound(message: string, subcode?: string): AppError {
  return new AppError("not_found", message, subcode ? { meta: { subcode } } : undefined);
}

export function forbidden(message: string, subcode?: string): AppError {
  return new AppError("forbidden", message, subcode ? { meta: { subcode } } : undefined);
}

export function invalidState(message: string, subcode?: string): AppError {
  return new AppError(
    "invalid_state",
    message,
    subcode ? { meta: { subcode } } : undefined,
  );
}

export function validation(
  message: string,
  fieldErrors?: Record<string, string>,
  details?: Record<string, unknown>,
): AppError {
  return new AppError("validation", message, { fieldErrors, details });
}

export function conflict(message: string, subcode?: string): AppError {
  return new AppError("conflict", message, subcode ? { meta: { subcode } } : undefined);
}

export function externalService(
  serviceName: string,
  message: string,
  cause?: unknown,
): AppError {
  return new AppError("external_service", message, {
    cause,
    meta: { service: serviceName },
  });
}

export function internal(message: string, cause?: unknown): AppError {
  return new AppError("internal", message, { cause });
}
